import { createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import api from '../../services/api';

export const adjustVendorRecoveryCount = createAction('vendor/adjustVendorRecoveryCount');

const getErrorMessage = (error, fallbackMessage) => (
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallbackMessage
);

const mergeOrderIntoList = (orders, updatedOrder) => {
  if (!updatedOrder) {
    return orders;
  }

  const updatedOrderId = String(updatedOrder.id ?? updatedOrder.order_id ?? '');

  if (!updatedOrderId) {
    return orders;
  }

  let found = false;

  const nextOrders = (orders || []).map((order) => {
    const currentId = String(order?.id ?? order?.order_id ?? '');
    if (currentId === updatedOrderId) {
      found = true;
      return {
        ...order,
        ...updatedOrder,
      };
    }
    return order;
  });

  if (!found) {
    nextOrders.unshift(updatedOrder);
  }

  return nextOrders;
};

const extractOrders = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.orders)) {
    return payload.orders;
  }

  if (Array.isArray(payload?.orders?.data)) {
    return payload.orders.data;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.results)) {
    return payload.results;
  }

  return [];
};

const fetchVendorRead = async (endpoint, { dispatch, signal }) => {
  let recoveryStarted = false;

  try {
    return await api.getWithRetry(endpoint, {
      signal,
      onRetry: () => {
        if (!recoveryStarted) {
          recoveryStarted = true;
          dispatch(adjustVendorRecoveryCount(1));
        }
      },
    });
  } finally {
    if (recoveryStarted) {
      dispatch(adjustVendorRecoveryCount(-1));
    }
  }
};

export const fetchVendorDashboard = createAsyncThunk(
  'vendor/fetchDashboard',
  async (_, { dispatch, rejectWithValue, signal }) => {
    try {
      const { data } = await fetchVendorRead('/vendor/dashboard', { dispatch, signal });
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch vendor dashboard'));
    }
  }
);

export const fetchVendorOrders = createAsyncThunk(
  'vendor/fetchOrders',
  async (params = {}, { dispatch, rejectWithValue, signal }) => {
    try {
      const searchParams = new URLSearchParams();

      if (params.status) searchParams.set('status', params.status);
      if (params.page) searchParams.set('page', params.page);
      if (params.per_page) searchParams.set('per_page', params.per_page);

      const query = searchParams.toString();
      const endpoint = query ? `/vendor/orders?${query}` : '/vendor/orders';

      const { data } = await fetchVendorRead(endpoint, { dispatch, signal });
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch vendor orders'));
    }
  }
);

export const fetchVendorOrderDetail = createAsyncThunk(
  'vendor/fetchOrderDetail',
  async (orderId, { dispatch, rejectWithValue, signal }) => {
    try {
      const { data } = await fetchVendorRead(`/vendor/orders/${orderId}`, { dispatch, signal });
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch vendor order'));
    }
  }
);

export const acceptOrder = createAsyncThunk(
  'vendor/acceptOrder',
  async (orderId, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/vendor/orders/${orderId}/accept`);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to accept order'));
    }
  }
);

export const rejectOrder = createAsyncThunk(
  'vendor/rejectOrder',
  async ({ orderId, reason }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/vendor/orders/${orderId}/reject`, {
        reason,
      });
      return data;
    } catch (error) {
      if ([404, 405].includes(error?.response?.status)) {
        try {
          const { data } = await api.patch(`/vendor/orders/${orderId}/update_status`, {
            status: 'rejected',
            reason,
          });
          return data;
        } catch (fallbackError) {
          return rejectWithValue(getErrorMessage(fallbackError, 'Failed to reject order'));
        }
      }

      return rejectWithValue(getErrorMessage(error, 'Failed to reject order'));
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  'vendor/updateOrderStatus',
  async ({ orderId, status, metadata = {} }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/vendor/orders/${orderId}/update_status`, {
        status,
        ...metadata,
      });
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update order status'));
    }
  }
);

export const fetchPerformance = createAsyncThunk(
  'vendor/fetchPerformance',
  async (params = {}, { dispatch, rejectWithValue, signal }) => {
    try {
      const searchParams = new URLSearchParams();

      if (params.period) searchParams.set('period', params.period);

      const query = searchParams.toString();
      const endpoint = query ? `/vendor/performance?${query}` : '/vendor/performance';

      const { data } = await fetchVendorRead(endpoint, { dispatch, signal });
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch vendor performance'));
    }
  }
);

export const fetchProfile = createAsyncThunk(
  'vendor/fetchProfile',
  async (_, { dispatch, rejectWithValue, signal }) => {
    try {
      const { data } = await fetchVendorRead('/vendor/profile', { dispatch, signal });
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch vendor profile'));
    }
  }
);

export const fetchVendorQuoteRequests = createAsyncThunk(
  'vendor/fetchQuoteRequests',
  async (params = {}, { dispatch, rejectWithValue, signal }) => {
    try {
      const searchParams = new URLSearchParams();
      if (params.page) searchParams.set('page', params.page);
      if (params.per_page) searchParams.set('per_page', params.per_page);

      const query = searchParams.toString();
      const endpoint = query ? `/vendor/quote_requests?${query}` : '/vendor/quote_requests';

      const { data } = await fetchVendorRead(endpoint, { dispatch, signal });
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch quote requests'));
    }
  }
);

export const fetchVendorQuoteRequestDetail = createAsyncThunk(
  'vendor/fetchQuoteRequestDetail',
  async (quoteRequestId, { dispatch, rejectWithValue, signal }) => {
    try {
      const { data } = await fetchVendorRead(`/vendor/quote_requests/${quoteRequestId}`, { dispatch, signal });
      return data.quote_request || data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch quote request'));
    }
  }
);

export const submitVendorQuote = createAsyncThunk(
  'vendor/submitQuote',
  async ({ quoteRequestId, priceCents, leadTimeDays, notes, validUntil }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/vendor/quote_requests/${quoteRequestId}/submit_quote`, {
        price_cents: priceCents,
        lead_time_days: leadTimeDays,
        notes,
        valid_until: validUntil,
      });
      return data.quote_request || data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to submit quote'));
    }
  }
);

export const declineVendorQuoteRequest = createAsyncThunk(
  'vendor/declineQuoteRequest',
  async ({ quoteRequestId, reason }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`/vendor/quote_requests/${quoteRequestId}/decline`, {
        reason,
      });
      return data.quote_request || data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to decline quote request'));
    }
  }
);

export const updateProfile = createAsyncThunk(
  'vendor/updateProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      const { data } = await api.patch('/vendor/profile', profileData);
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update vendor profile'));
    }
  }
);

export const fetchMoldInventory = createAsyncThunk(
  'vendor/fetchMoldInventory',
  async (_, { dispatch, rejectWithValue, signal }) => {
    try {
      const { data } = await fetchVendorRead('/vendor/mold_inventory', { dispatch, signal });
      return data.mold_inventory || data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch mold inventory'));
    }
  }
);

export const saveMoldInventory = createAsyncThunk(
  'vendor/saveMoldInventory',
  async (moldInventory, { rejectWithValue }) => {
    try {
      const { data } = await api.put('/vendor/mold_inventory', {
        mold_inventory: moldInventory,
      });
      return data.mold_inventory || data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to save mold inventory'));
    }
  }
);

export const fetchFastDeliveryItems = createAsyncThunk(
  'vendor/fetchFastDeliveryItems',
  async (_, { dispatch, rejectWithValue, signal }) => {
    try {
      const { data } = await fetchVendorRead('/vendor/fast_delivery_items', { dispatch, signal });
      return data.fast_delivery_items || [];
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to fetch fast-delivery items'));
    }
  }
);

export const createFastDeliveryItem = createAsyncThunk(
  'vendor/createFastDeliveryItem',
  async (fastDeliveryItem, { rejectWithValue }) => {
    try {
      const { data } = await api.post('/vendor/fast_delivery_items', {
        fast_delivery_item: fastDeliveryItem,
      });
      return data.fast_delivery_item || data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to publish fast-delivery item'));
    }
  }
);

export const updateFastDeliveryItem = createAsyncThunk(
  'vendor/updateFastDeliveryItem',
  async ({ itemId, fastDeliveryItem }, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`/vendor/fast_delivery_items/${itemId}`, {
        fast_delivery_item: fastDeliveryItem,
      });
      return data.fast_delivery_item || data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to update fast-delivery item'));
    }
  }
);

export const archiveFastDeliveryItem = createAsyncThunk(
  'vendor/archiveFastDeliveryItem',
  async (itemId, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/vendor/fast_delivery_items/${itemId}`);
      return data.fast_delivery_item || data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to archive fast-delivery item'));
    }
  }
);

export const downloadProductionFiles = createAsyncThunk(
  'vendor/downloadProductionFiles',
  async (orderId, { dispatch, rejectWithValue, signal }) => {
    try {
      const { data } = await fetchVendorRead(`/vendor/orders/${orderId}/production_files`, { dispatch, signal });
      return data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error, 'Failed to download production files'));
    }
  }
);

const initialState = {
  dashboard: null,
  orders: [],
  currentOrder: null,
  orderDetailError: null,
  performance: null,
  profile: null,
  quoteRequests: [],
  quoteRequestsMeta: null,
  currentQuoteRequest: null,
  moldInventory: {
    single_wall: [],
    double_wall: [],
  },
  fastDeliveryItems: [],
  fastDeliveryLoading: false,
  recoveryCount: 0,
  loading: false,
  error: null,
};

const vendorSlice = createSlice({
  name: 'vendor',
  initialState,
  reducers: {
    clearVendorError: (state) => {
      state.error = null;
    },
    clearCurrentVendorOrder: (state) => {
      state.currentOrder = null;
      state.orderDetailError = null;
    },
    clearCurrentVendorQuoteRequest: (state) => {
      state.currentQuoteRequest = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(adjustVendorRecoveryCount, (state, action) => {
        state.recoveryCount = Math.max(0, (state.recoveryCount || 0) + action.payload);
      })
      .addCase(fetchVendorDashboard.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendorDashboard.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.dashboard = action.payload?.dashboard || action.payload;
      })
      .addCase(fetchVendorDashboard.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchVendorOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendorOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.orders = extractOrders(action.payload);
      })
      .addCase(fetchVendorOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchVendorOrderDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.orderDetailError = null;
      })
      .addCase(fetchVendorOrderDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.orderDetailError = null;
        const order = action.payload?.order || action.payload;
        state.currentOrder = order;
        state.orders = mergeOrderIntoList(state.orders, order);
      })
      .addCase(fetchVendorOrderDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.orderDetailError = action.payload;
      })
      .addCase(acceptOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(acceptOrder.fulfilled, (state, action) => {
        state.loading = false;
        const order = action.payload?.order || action.payload?.vendor_order || action.payload;
        state.currentOrder = order;
        state.orders = mergeOrderIntoList(state.orders, order);
      })
      .addCase(acceptOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(rejectOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(rejectOrder.fulfilled, (state, action) => {
        state.loading = false;
        const order = action.payload?.order || action.payload?.vendor_order || action.payload;
        state.currentOrder = order;
        state.orders = mergeOrderIntoList(state.orders, order);
      })
      .addCase(rejectOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateOrderStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateOrderStatus.fulfilled, (state, action) => {
        state.loading = false;
        const order = action.payload?.order || action.payload?.vendor_order || action.payload;
        state.currentOrder = order;
        state.orders = mergeOrderIntoList(state.orders, order);
      })
      .addCase(updateOrderStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchPerformance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPerformance.fulfilled, (state, action) => {
        state.loading = false;
        state.performance = action.payload?.performance || action.payload;
      })
      .addCase(fetchPerformance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload?.profile || action.payload;
      })
      .addCase(fetchProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchVendorQuoteRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendorQuoteRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.quoteRequests = action.payload?.quote_requests || [];
        state.quoteRequestsMeta = action.payload?.meta || null;
      })
      .addCase(fetchVendorQuoteRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchVendorQuoteRequestDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVendorQuoteRequestDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.currentQuoteRequest = action.payload;
        state.quoteRequests = mergeOrderIntoList(state.quoteRequests, action.payload);
      })
      .addCase(fetchVendorQuoteRequestDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(submitVendorQuote.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(submitVendorQuote.fulfilled, (state, action) => {
        state.loading = false;
        state.currentQuoteRequest = action.payload;
        state.quoteRequests = mergeOrderIntoList(state.quoteRequests, action.payload);
      })
      .addCase(submitVendorQuote.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(declineVendorQuoteRequest.fulfilled, (state, action) => {
        state.currentQuoteRequest = action.payload;
        state.quoteRequests = mergeOrderIntoList(state.quoteRequests, action.payload);
      })
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload?.profile || action.payload;
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMoldInventory.pending, (state) => {
        state.fastDeliveryLoading = true;
        state.error = null;
      })
      .addCase(fetchMoldInventory.fulfilled, (state, action) => {
        state.fastDeliveryLoading = false;
        state.moldInventory = action.payload?.mold_inventory || action.payload || initialState.moldInventory;
      })
      .addCase(fetchMoldInventory.rejected, (state, action) => {
        state.fastDeliveryLoading = false;
        state.error = action.payload;
      })
      .addCase(saveMoldInventory.pending, (state) => {
        state.fastDeliveryLoading = true;
        state.error = null;
      })
      .addCase(saveMoldInventory.fulfilled, (state, action) => {
        state.fastDeliveryLoading = false;
        state.moldInventory = action.payload?.mold_inventory || action.payload || initialState.moldInventory;
      })
      .addCase(saveMoldInventory.rejected, (state, action) => {
        state.fastDeliveryLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchFastDeliveryItems.pending, (state) => {
        state.fastDeliveryLoading = true;
        state.error = null;
      })
      .addCase(fetchFastDeliveryItems.fulfilled, (state, action) => {
        state.fastDeliveryLoading = false;
        state.fastDeliveryItems = action.payload || [];
      })
      .addCase(fetchFastDeliveryItems.rejected, (state, action) => {
        state.fastDeliveryLoading = false;
        state.error = action.payload;
      })
      .addCase(createFastDeliveryItem.pending, (state) => {
        state.fastDeliveryLoading = true;
        state.error = null;
      })
      .addCase(createFastDeliveryItem.fulfilled, (state, action) => {
        state.fastDeliveryLoading = false;
        state.fastDeliveryItems = mergeOrderIntoList(state.fastDeliveryItems, action.payload);
      })
      .addCase(createFastDeliveryItem.rejected, (state, action) => {
        state.fastDeliveryLoading = false;
        state.error = action.payload;
      })
      .addCase(updateFastDeliveryItem.fulfilled, (state, action) => {
        state.fastDeliveryItems = mergeOrderIntoList(state.fastDeliveryItems, action.payload);
      })
      .addCase(updateFastDeliveryItem.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(archiveFastDeliveryItem.fulfilled, (state, action) => {
        state.fastDeliveryItems = mergeOrderIntoList(state.fastDeliveryItems, action.payload);
      })
      .addCase(archiveFastDeliveryItem.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(downloadProductionFiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(downloadProductionFiles.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(downloadProductionFiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearVendorError, clearCurrentVendorOrder, clearCurrentVendorQuoteRequest } = vendorSlice.actions;

export default vendorSlice.reducer;
